/**
 * PostTracking.js
 * Handles impression and view tracking for posts
 * Batches events and sends them to the SAVE_POSTS_USERS API
 */
import { Postrequestcall } from "../../request/Postrequest";
import { SAVE_POSTS_USERS } from "../../constant/Apipath";

class PostTrackingManager {
  constructor() {
    // Store tracked events temporarily before sending to API
    this.impressionQueue = new Map(); // PostId -> impression data
    this.viewQueue = new Map(); // PostId -> view data

    // Track which posts have been impressed today (prevents duplicate impressions)
    this.impressedPostsToday = new Set();

    // Track which posts have been viewed in this session (optional unique views)
    this.viewedPostsSession = new Set();

    // User and token info
    this.userId = null;
    this.accessToken = null;

    // Batch sending configuration
    this.batchInterval = 10000; // Send every 10 seconds
    this.batchTimer = null;

    // Initialize date tracking
    this.currentDate = new Date().toDateString();

    // Start batch timer
    this.startBatchTimer();

    // Handle page unload to send remaining events
    this.setupUnloadHandler();
  }

  /**
   * Initialize with user credentials
   */
  initialize(userId, accessToken) {
    this.userId = userId;
    this.accessToken = accessToken;
  }

  /**
   * Check if we're on a new day and reset impression tracking
   */
  checkDateReset() {
    const today = new Date().toDateString();
    if (today !== this.currentDate) {
      this.currentDate = today;
      this.impressedPostsToday.clear();
    }
  }

  /**
   * Record an impression when a post becomes visible
   * Only counts once per day per post
   */
  recordImpression(postId) {
    if (!this.userId || !postId) {
      // console.warn(`⚠️ Cannot record impression - userId: ${this.userId}, postId: ${postId}`);
      return;
    }

    this.checkDateReset();

    // Check if already impressed today
    if (this.impressedPostsToday.has(postId)) {
      // console.log(`📊 Post ${postId} already impressed today, skipping`);
      return;
    }

    // Mark as impressed
    this.impressedPostsToday.add(postId);

    // Add to impression queue
    const now = new Date();
    const timestamp = this.formatDateTime(now);

    this.impressionQueue.set(postId, {
      Id: 0,
      PostId: postId,
      UserId: this.userId,
      ImpressionLastDate: timestamp,
      NumberOfImpressions: 1,
      NumberOfViews: 0,
      ViewerDate: "",
      ModifiedBy: this.userId
    });

    console.log(`📊 Impression recorded for post ${postId} at ${timestamp}`, {
      queueSize: this.impressionQueue.size,
      totalQueued: this.impressionQueue.size + this.viewQueue.size
    });
  }

  /**
   * Record a view when user interacts with a post
   * Counts every interaction (or can be made unique per session)
   */
  recordView(postId, uniquePerSession = false) {
    if (!this.userId || !postId) return;

    // Optional: Only count unique views per session
    if (uniquePerSession && this.viewedPostsSession.has(postId)) {
      return;
    }

    if (uniquePerSession) {
      this.viewedPostsSession.add(postId);
    }

    // Add to view queue
    const now = new Date();
    const timestamp = this.formatDateTime(now);

    // If this post already has an impression queued, combine them
    if (this.impressionQueue.has(postId)) {
      const existing = this.impressionQueue.get(postId);
      existing.NumberOfViews = 1;
      existing.ViewerDate = timestamp;
      // Move to view queue
      this.viewQueue.set(postId, existing);
      this.impressionQueue.delete(postId);
    } else {
      this.viewQueue.set(postId, {
        Id: 0,
        PostId: postId,
        UserId: this.userId,
        ImpressionLastDate: "",
        NumberOfImpressions: 0,
        NumberOfViews: 1,
        ViewerDate: timestamp,
        ModifiedBy: this.userId
      });
    }

    // console.log(`👁️ View recorded for post ${postId}`);
  }

  /**
   * Format date to match API format: "2025-11-17 09:32:27"
   */
  formatDateTime(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");

    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  }

  /**
   * Send batched events to API
   */
  async sendBatch() {
    if (!this.userId || !this.accessToken) return;

    // Combine all queued events
    const allEvents = [
      ...Array.from(this.impressionQueue.values()),
      ...Array.from(this.viewQueue.values())
    ];

    if (allEvents.length === 0) return;

    try {
      const payload = {
        UserId: this.userId,
        ReturnRecordId: true,
        ReturnRecordError: true,
        SaveList: allEvents
      };
      //  console.log(`📤 Sending ${allEvents.length} post tracking events to API`, payload);
      const response = await Postrequestcall(SAVE_POSTS_USERS, payload, this.accessToken);
      // console.log(`📥 API Response:`, response?.status, response?.data);
      // Check for successful response (status 200 or OK message)
      if (response?.status === 200 || response?.data?.message === "OK") {
        //console.log(`✅ Successfully saved ${allEvents.length} post tracking events`);
        // Clear queues after successful send
        this.impressionQueue.clear();
        this.viewQueue.clear();
      } else {
        console.warn("⚠️ Post tracking API returned unexpected response", {
          status: response?.status,
          data: response?.data,
          message: response?.data?.message
        });
      }
    } catch (error) {
      console.error("❌ Error sending post tracking batch:", error?.message, error);
      // Keep events in queue for retry on next batch
    }
  }

  /**
   * Start automatic batch sending timer
   */
  startBatchTimer() {
    if (this.batchTimer) {
      clearInterval(this.batchTimer);
    }

    this.batchTimer = setInterval(() => {
      this.sendBatch();
    }, this.batchInterval);
  }

  /**
   * Stop batch timer
   */
  stopBatchTimer() {
    if (this.batchTimer) {
      clearInterval(this.batchTimer);
      this.batchTimer = null;
    }
  }

  /**
   * Setup handler to send remaining events on page unload
   */
  setupUnloadHandler() {
    // Use beforeunload to send final batch
    window.addEventListener("beforeunload", () => {
      // Send synchronously if possible (may not work in all browsers)
      if (navigator.sendBeacon && this.accessToken) {
        const allEvents = [
          ...Array.from(this.impressionQueue.values()),
          ...Array.from(this.viewQueue.values())
        ];

        if (allEvents.length > 0) {
          const payload = {
            UserId: this.userId,
            ReturnRecordId: true,
            ReturnRecordError: true,
            SaveList: allEvents
          };

          // Use sendBeacon for reliability on page unload
          const blob = new Blob([JSON.stringify(payload)], {
            type: "application/json"
          });

          // Note: You'll need to adjust the endpoint URL
          navigator.sendBeacon(`${import.meta.env.REACT_APP_BASE_URL}/${SAVE_POSTS_USERS}`, blob);
        }
      }
    });

    // Also handle visibility change (when user switches tabs)
    document.addEventListener("visibilitychange", () => {
      if (document.hidden) {
        this.sendBatch();
      }
    });
  }

  /**
   * Manual trigger to send batch immediately
   */
  async flushNow() {
    await this.sendBatch();
  }

  /**
   * Clear all tracking data (useful for logout)
   */
  reset() {
    this.impressionQueue.clear();
    this.viewQueue.clear();
    this.impressedPostsToday.clear();
    this.viewedPostsSession.clear();
    this.userId = null;
    this.accessToken = null;
  }
}

// Create singleton instance
const postTrackingManager = new PostTrackingManager();

export default postTrackingManager;
